/**
 * This custom hook provides the functionality to save a user with the given parameters.
 */

import React, {useEffect, useState} from "react";
import useLoadSelectableScoredScenesInPlaylist from "./useLoadSelectableScoredScenesInPlaylist";
import moment from "moment";

const useGeneralEditPlaylistForm = (selectedPlaylist, editedPlaylist) => {

    // Editable values (video values) - used to control the controlled elements' states
    const [name, setName] = useState(selectedPlaylist?.name);
    const [association, setAssociation] = React.useState(selectedPlaylist?.association || ''); // Will be set automatically - see "useEffect()"
    const [feedbackMode, setFeedbackMode] = React.useState(selectedPlaylist?.feedbackMode || 'standard');
    const [scoredScenesDestination, setScoredScenesDestination] = useState(selectedPlaylist?.scoredScenes || [])

    // Maintains the source (if the key is undefined, all playlists will be selectable as source)
    const {
        loading,
        scoredScenesSource,
        setScoredScenesSource
    } = useLoadSelectableScoredScenesInPlaylist(selectedPlaylist?.key)

    // Search fields
    const [associationSearch, setAssociationSearch] = React.useState([]);
    const [disciplineSearch, setDisciplineSearch] = React.useState([]);
    const [difficultySearch, setDifficultySearch] = React.useState([]);
    const [mainFocusSearch, setMainFocusSearch] = React.useState([]);


    // If the some relevant state changes, also the reference needs to be updated
    useEffect(() => {
        editedPlaylist.current = {
            ...editedPlaylist.current,
            key: selectedPlaylist?.key,
            name: name,
            association: association,
            feedbackMode: feedbackMode,
            scoredScenes: scoredScenesDestination
        }
    }, [name, association, feedbackMode, scoredScenesDestination])

    // Automatically set the association based on the scored scenes added to the playlist
    useEffect(() => {
        if (scoredScenesDestination?.length > 0) {
            setAssociation(scoredScenesDestination[0].video.basics.association)
        } else {
            setAssociation('')
        }
    }, [scoredScenesDestination])

    const getPlaylistDuration = () => {
        let totalDuration = 0
        scoredScenesDestination.forEach((scoredScene) => {
            const sceneDuration = scoredScene.basics.sceneEnd - scoredScene.basics.sceneStart
            totalDuration += sceneDuration
        })
        return moment((totalDuration) * 1000).format("mm:ss")
    }

    const handleChangeName = (event) => {
        setName(event.target.value)
    }

    const handleChangeFeedbackMode = (event, value) => {
        if (value !== null) {
            setFeedbackMode(value)
        }
    }

    // Search handlers
    const handleChangeAssociationSearch = (event, value) => {
        setAssociationSearch(value)
    }

    const handleChangeDisciplineSearch = (event, value) => {
        setDisciplineSearch(value)
    }

    const handleChangeDifficultySearch = (event, value) => {
        setDifficultySearch(value)
    }

    const handleChangeMainFocusSearch = (event, value) => {
        setMainFocusSearch(value)
    }

    /*
    Handles the case if entries are changed within a list
     */
    const rearrangeList = (list, start, end) => {
        // Make a copy of the list
        const result = Array.from(list)

        // Delete from the start-drag position
        const [deletedElement] = result.splice(start, 1)

        // Insert it again after dragging
        result.splice(end, 0, deletedElement)

        return result
    }

    /*
    Handles the case if entries are dragged among lists
     */
    const moveFromTo = (source, destination, droppableSource, droppableDestination, draggedScoredSceneKey) => {
        // Make copies from the source and destination list
        const sourceCopy = Array.from(source)
        const destinationCopy = Array.from(destination)

        // Matching by index alone is not possible, because of the filtering function - therefore get the fresh index by the scored scene key
        const sourceIndex = sourceCopy.findIndex((scoredScene) => scoredScene.key === draggedScoredSceneKey)

        // Remove from source list
        const [deleted] = sourceCopy.splice(sourceIndex, 1)

        // Insert the removed element in the destination list
        destinationCopy.splice(droppableDestination.index, 0, deleted)

        // Prepare the actual lists
        const result = {}
        result[droppableSource.droppableId] = sourceCopy
        result[droppableDestination.droppableId] = destinationCopy

        return result
    }

    /*
    Handles the complete drag & drop process within and among lists
     */
    const handleDragEnd = (result) => {
        const {source, destination} = result

        // Dropping in empty area
        if (!destination) {
            return
        }

        // Drag & drop within list
        if (source.droppableId === destination.droppableId) {
            const items = rearrangeList(
                source.droppableId === 'scoredScenesSource' ? scoredScenesSource : scoredScenesDestination,
                source.index,
                destination.index
            );

            if (source.droppableId === 'scoredScenesSource') {
                setScoredScenesSource(items)
            } else {
                setScoredScenesDestination(items)
            }
        }
        // Drag & drop among lists
        else {
            if (source.droppableId === 'scoredScenesSource') {
                const moveResult = moveFromTo(
                    scoredScenesSource,
                    scoredScenesDestination,
                    source,
                    destination,
                    result.draggableId
                );

                setScoredScenesSource(moveResult['scoredScenesSource'])
                setScoredScenesDestination(moveResult['scoredScenesDestination'])
            } else {
                const moveResult = moveFromTo(
                    scoredScenesDestination,
                    scoredScenesSource,
                    source,
                    destination,
                    result.draggableId
                );

                setScoredScenesSource(moveResult['scoredScenesSource'])
                setScoredScenesDestination(moveResult['scoredScenesDestination'])
            }
        }
    }

    return {
        scoredScenesSource,
        scoredScenesDestination,
        loading,
        association,
        name,
        handleChangeName,
        feedbackMode,
        handleChangeFeedbackMode,
        difficultySearch,
        handleChangeDifficultySearch,
        mainFocusSearch,
        handleChangeMainFocusSearch,
        disciplineSearch,
        handleChangeDisciplineSearch,
        associationSearch,
        handleChangeAssociationSearch,
        handleDragEnd,
        getPlaylistDuration
    }
}
export default useGeneralEditPlaylistForm;